import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "react-query";
import { X } from "lucide-react";
import toast from "react-hot-toast";
import { apiService } from "../services/api";
import LoadingSpinner from "../components/LoadingSpinner";
import TypeaheadUserInput from "../components/TypeaheadUserInput";
import TypeaheadPrefixInput from "../components/TypeaheadPrefixInput";
import type { DtAPIKey, DtAPIKeyPrefix, DtAPIKeyOwner, User } from "../types";

export default function EditDtApiKeyPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [owner, setOwner] = useState("");
  const [isActive, setIsActive] = useState(true);
  const [prefixes, setPrefixes] = useState<string[]>([]);
  const [selectedOwnerIds, setSelectedOwnerIds] = useState<string[]>([]);
  const [ownerPickKey, setOwnerPickKey] = useState(0);

  const { data: response, isLoading } = useQuery(
    ["dt-api-key", id],
    () => apiService.getDtAPIKey(id!),
    { enabled: !!id },
  );

  const apiKey = response?.data?.data as DtAPIKey | undefined;

  const { data: usersData } = useQuery("users", () => apiService.getUsers());
  const allUsers: User[] = usersData?.data?.data || [];

  useEffect(() => {
    if (apiKey) {
      setOwner(apiKey.owner);
      setIsActive(apiKey.isActive);
      setPrefixes(
        apiKey.prefixes?.map((p: DtAPIKeyPrefix) => p.allowedPrefix) ?? [],
      );
      setSelectedOwnerIds(
        apiKey.owners?.map((o: DtAPIKeyOwner) => o.userId) ?? [],
      );
    }
  }, [apiKey]);

  const updateMutation = useMutation(
    () =>
      apiService.updateDtAPIKey(id!, {
        owner,
        isActive,
        prefixes,
        ownerIds: selectedOwnerIds,
      }),
    {
      onSuccess: () => {
        toast.success("API key updated");
        queryClient.invalidateQueries(["dt-api-keys"]);
        queryClient.invalidateQueries(["dt-api-key", id]);
        navigate("/dt-api-keys");
      },
      onError: (_error: any) => {
        toast.error("Failed to update API key");
      },
    },
  );

  const handleRemovePrefix = (index: number) => {
    setPrefixes(prefixes.filter((_, i) => i !== index));
  };

  if (isLoading) return <LoadingSpinner />;

  if (!apiKey) {
    return (
      <div className="text-center py-12 text-gray-500 dark:text-gray-400">
        API key not found.
      </div>
    );
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
        Edit Dependency Track API Key
      </h1>

      <div className="card p-6 space-y-4">
        {apiKey.rawKey && (
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              API Key
            </label>
            <code className="block p-2 bg-gray-50 dark:bg-gray-900 border border-gray-300 dark:border-gray-600 rounded text-sm font-mono">
              {apiKey.rawKey}
            </code>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Owner (team name)
          </label>
          <input
            type="text"
            value={owner}
            onChange={(e) => setOwner(e.target.value)}
            className="input w-full"
          />
        </div>

        <div>
          <label className="flex items-center gap-2 text-sm font-medium text-gray-700 dark:text-gray-300">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="rounded border-gray-300 dark:border-gray-600"
            />
            Active
          </label>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Allowed Prefixes
          </label>
          <TypeaheadPrefixInput
            onSelect={(value) => {
              if (!prefixes.includes(value)) {
                setPrefixes([...prefixes, value]);
              }
            }}
            excludeValues={prefixes}
          />
          <div className="flex flex-wrap gap-2">
            {prefixes.map((prefix, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-1 px-2 py-1 rounded bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 text-sm"
              >
                {prefix}
                <button
                  onClick={() => handleRemovePrefix(i)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                >
                  <X className="h-3 w-3" />
                </button>
              </span>
            ))}
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Key Owners
          </label>
          <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
            Users who can view and manage this API key.
          </p>
          <TypeaheadUserInput
            key={ownerPickKey}
            id="owner-search"
            name="owner-search"
            value=""
            onChange={(userId: string) => {
              const matchedUser = allUsers.find((u) => u.id === userId);
              if (!matchedUser) return;
              if (!selectedOwnerIds.includes(userId)) {
                setSelectedOwnerIds([...selectedOwnerIds, userId]);
              }
              setOwnerPickKey((k) => k + 1);
            }}
            users={allUsers.filter((u) => !selectedOwnerIds.includes(u.id))}
            placeholder="Search users..."
          />
          <div className="flex flex-wrap gap-2 mt-2">
            {selectedOwnerIds.map((uid) => {
              const user = allUsers.find((u) => u.id === uid);
              const ownerEntry = apiKey?.owners?.find(
                (o: DtAPIKeyOwner) => o.userId === uid,
              );
              const displayName = user?.name || ownerEntry?.user?.name || uid;
              return (
                <span
                  key={uid}
                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 text-sm"
                >
                  {displayName}
                  <button
                    onClick={() =>
                      setSelectedOwnerIds(
                        selectedOwnerIds.filter((oid) => oid !== uid),
                      )
                    }
                    className="text-blue-400 hover:text-blue-600 dark:hover:text-blue-200"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              );
            })}
          </div>
        </div>

        <div className="flex gap-3 pt-4">
          <button
            onClick={() => updateMutation.mutate()}
            disabled={!owner.trim() || updateMutation.isLoading}
            className="btn btn-primary"
          >
            {updateMutation.isLoading ? "Saving..." : "Save Changes"}
          </button>
          <button
            onClick={() => navigate("/dt-api-keys")}
            className="btn btn-secondary"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}
